import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Target } from 'lucide-react';
import CrmCoreListPage from './components/CrmCoreListPage';
import { useCrmSalesAgentsPicker } from './crmPickerOptions';

const SalesGoalsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.crm.pages.salesGoalsPage.${key}`, opts);
  const { agentOptions } = useCrmSalesAgentsPicker();

  const typeOptions = useMemo(() => [
    { value: 'revenue', label: p('type_revenue') },
    { value: 'deals', label: p('type_deals') },
    { value: 'meetings', label: p('type_meetings') },
    { value: 'calls', label: p('type_calls') },
  ], [p]);

  const periodOptions = useMemo(() => [
    { value: 'daily', label: p('period_daily') },
    { value: 'weekly', label: p('period_weekly') },
    { value: 'monthly', label: p('period_monthly') },
    { value: 'quarterly', label: p('period_quarterly') },
    { value: 'yearly', label: p('period_yearly') },
  ], [p]);

  const statusOptions = useMemo(() => [
    { value: 'active', label: p('status_active') },
    { value: 'completed', label: p('status_completed') },
    { value: 'failed', label: p('status_failed') },
    { value: 'cancelled', label: p('status_cancelled') },
  ], [p]);

  return (
    <CrmCoreListPage
      pageKey="salesGoalsPage"
      apiPath="/crm/sales-goals"
      icon={Target}
      entityRef={{ field: 'goal_ref', prefixKey: 'salesGoal', fallback: 'GOL' }}
      statusOptions={statusOptions}
      mapRecord={(r) => {
        const target = parseFloat(r.target_value) || 0;
        const current = parseFloat(r.current_value) || 0;
        const progress = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
        const typeOpt = typeOptions.find((o) => o.value === r.type);
        const periodOpt = periodOptions.find((o) => o.value === r.period);
        return {
          ...r,
          title: r.title || r.goal_title,
          type_label: typeOpt?.label || r.type || '—',
          period_label: periodOpt?.label || r.period || '—',
          progress_label: `${progress}%`,
        };
      }}
      buildPayload={(form) => ({
        title: form.title,
        type: form.type || 'revenue',
        period: form.period || 'monthly',
        target_value: parseFloat(form.target_value) || 0,
        current_value: parseFloat(form.current_value) || 0,
        assigned_to: form.assigned_to || '',
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        status: form.status || 'active',
        description: form.description || '',
      })}
      fields={[
        { name: 'title', labelKey: 'field_title', required: true, placeholderKey: 'field_title_ph' },
        { name: 'type', labelKey: 'field_type', type: 'select', options: typeOptions, defaultValue: 'revenue' },
        { name: 'period', labelKey: 'field_period', type: 'select', options: periodOptions, defaultValue: 'monthly' },
        { name: 'assigned_to', labelKey: 'field_assigned', type: 'select', options: agentOptions, placeholderKey: 'field_assigned_ph' },
        { name: 'target_value', labelKey: 'field_target', inputType: 'number' },
        { name: 'current_value', labelKey: 'field_current', inputType: 'number' },
        { name: 'start_date', labelKey: 'field_start_date', inputType: 'date' },
        { name: 'end_date', labelKey: 'field_end_date', inputType: 'date' },
        { name: 'status', labelKey: 'field_status', type: 'select', options: statusOptions, defaultValue: 'active' },
        { name: 'description', labelKey: 'field_description', type: 'textarea', fullWidth: true, placeholderKey: 'field_description_ph' },
      ]}
      columns={[
        { key: 'goal_ref', labelKey: 'col_ref' },
        { key: 'title', labelKey: 'col_title' },
        { key: 'type_label', labelKey: 'col_type' },
        { key: 'period_label', labelKey: 'col_period' },
        { key: 'target_value', labelKey: 'col_target', type: 'money' },
        { key: 'current_value', labelKey: 'col_current', type: 'money' },
        { key: 'progress_label', labelKey: 'col_progress' },
        { key: 'status', labelKey: 'col_status', type: 'status' },
      ]}
    />
  );
};

export default SalesGoalsPage;
